import { ColDef, ValueFormatterParams, ValueGetterParams } from '@ag-grid-community/core';
import { Injectable } from '@angular/core';
import {
  AssetType,
  Criticality,
  PolicyFindingFilterField,
  EventCategoryType,
  FindingStatus,
  PolicyEventType,
  PolicyLevel,
  PurdueLevel,
} from '@uigy/generated/graphql';
import { LanguageService } from '@uigy/shared/services/language.service';
import { GridFormatterService } from '@uigy/shared/modules/grid/formatters/grid-formatter.service';
import { InventoryUiGridService } from '@uigy/modules/inventory/services/inventory-ui-grid.service';
import { PluginsGridSettingsService } from '@uigy/shared/plugins-grid/services/plugins-grid-settings.service';
import {
  SeverityCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/severity-cell-renderer/severity-cell-renderer.component';
import { UiGridFilterModel } from '@uigy/shared/components/ui-grid/models/ui-grid';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import Utils from '@uigy/utils';
import {
  SelectableFilterComponent,
} from '@uigy/shared/modules/grid/filters/selectable-filter/selectable-filter.component';
import { useIpsKeyComponentMap } from '@uigy/shared/modules/grid/filters/selectable-filter/constants';
import { SelectableFilterFilterParams } from '@uigy/shared/modules/grid/filters/selectable-filter/interface';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import {
  PolicyFindingsService,
} from '@uigy/modules/findings/components/findings-policy-violations/services/policy-findings.service';
import { Maybe } from 'graphql/jsutils/Maybe';
import { assetTypesMap, getAsset } from '@uigy/shared/constants/assetTypes.constant';
import { DelimitersEnum } from '@uigy/shared/enums/delimiters.enum';
import { ColumnFilterItem } from '@uigy/shared/modules/grid/models/grid';
import {
  createFilterComponentMap,
} from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.interfaces';
import {
  TimeFilterSelectorComponent,
} from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.component';
import { TextListFilterComponent } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.component';
import { TextListFilterParams } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.interfaces';

@Injectable({
  providedIn: 'root',
})
export class PolicyFindingsGridSettingsService {

  public readonly findingsTexts = this.languageService.texts.findings.policy_violations.grid;

  public get findingsColumnDefs() {
    return [
      this.FINDING_ID,
      this.STATUS,
      this.SEVERITY,
      this.VIOLATION_TYPE,
      this.VIOLATION_CATEGORY,
      this.POLICY,
      this.PLUGIN_NAME,
      this.MITRE_TACTICS,
      this.MITRE_TECHNIQUE,
      this.SRC_ASSETS,
      this.SRC_IP,
      this.SRC_MACS,
      this.DST_ASSETS,
      this.DST_IP,
      this.DST_MACS,
      this.PROTOCOL,
      this.FIRST_HIT,
      this.LAST_HIT,
      this.ACTIVE_HITS,
      this.ASSET_TYPE,
      this.ASSET_CRITICALITY,
      this.ASSET_VENDOR,
      this.ASSET_FAMILY,
      this.ASSET_MODEL,
      // this.ASSET_TAGS,
      this.PURDUE,
      this.ASSET_LOCATION,
      this.RESOLVED_ON,
      this.RESOLVED_BY,
      this.COMMENT
    ];
  }

  private readonly FINDING_ID: ColDef = {
    headerName: this.findingsTexts.id,
    colId: PolicyFindingFilterField.Id,
    field: 'id',
    initialWidth: 200,
    enableRowGroup: false,
    hide: true,
    cellRenderer: 'copyTextRendererComponent',
    filter: TextListFilterComponent,
    filterParams: {
      title: this.findingsTexts.id,
      validationType: 'uuid',
    } as TextListFilterParams,
  };

  public readonly STATUS: ColDef = {
    headerName: this.findingsTexts.status,
    colId: PolicyFindingFilterField.Status,
    field: 'status',
    valueFormatter: (params: ValueFormatterParams): string => this.gridFormatterService.FindingStatusFormatter(params.value),
    filterParams: {
      suppressSorting: true,
      values: Object.values(FindingStatus),
      valueFormatter: this.gridFormatterService.FindingStatusFormatter,
    },
    initialWidth: 152,
  };

  public readonly SEVERITY: ColDef = {
    ...this.pluginsGridSettingsService.SEVERITY_COLUMN,
    headerName: this.findingsTexts.severity,
    colId: PolicyFindingFilterField.Severity,
    field: 'severity',
    sort: 'desc',
    initialWidth: 120,
    cellRenderer: SeverityCellRendererComponent,
    tooltipValueGetter: (params) => this.languageService.formatEnum('PolicyLevel', params.value) ?? '',
    comparator: Utils.sortByPolicySeverityLevel,
    filterParams: {
      sortComparator: (a: ColumnFilterItem<PolicyLevel>, b: ColumnFilterItem<PolicyLevel>) =>
        Utils.sortByPolicySeverityLevel(a.name || PolicyLevel.None, b.name || PolicyLevel.None)
    },
  };

  public readonly VIOLATION_TYPE: ColDef = {
    headerName: this.findingsTexts.violation_type,
    colId: PolicyFindingFilterField.EventType,
    field: 'eventType.type',
    valueGetter: (params) => {
      return params?.data?.eventType?.type ?? params?.data?.eventType;
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return this.policyFindingsService.getPolicyViolationType(params?.value ?? '');
    },
    filterParams: {
      valueFormatter: (params: PolicyEventType) => {
        return this.policyFindingsService.getPolicyViolationType(params ?? '');
      },
    },
    initialWidth: 200,
  };

  public readonly VIOLATION_CATEGORY: ColDef = {
    headerName: this.findingsTexts.violation_category,
    colId: PolicyFindingFilterField.Category,
    field: 'eventType.category',
    valueGetter: (params) => {
      return params?.data?.eventType?.category ?? params?.data?.category;
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return this.languageService.formatEnum('EventCategoryType', params.value);
    },
    filterParams: {
      valueFormatter: (value: EventCategoryType) => {
        return this.languageService.formatEnum('EventCategoryType', value);
      },
    },
    initialWidth: 200,
    hide: true,
  };

  public readonly POLICY: ColDef = {
    headerName: this.findingsTexts.policy_name,
    colId: PolicyFindingFilterField.PolicyTitle,
    field: 'policyTitle',
    initialWidth: 200,
    hide: true,
    valueGetter: (params: ValueGetterParams) => {
      return params?.data?.policyTitle ?? params?.data?.policy?.title;
    },
  };

  public readonly PLUGIN_NAME: ColDef = {
    headerName: this.findingsTexts.plugin_name,
    colId: PolicyFindingFilterField.PluginName,
    field: 'pluginName',
    initialWidth: 296,
    hide: true,
  };

  public readonly MITRE_TACTICS: ColDef = {
    headerName: this.findingsTexts.mitre_tactics,
    colId: PolicyFindingFilterField.MitreTactics,
    field: 'mitreTactics',
    initialWidth: 200,
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
    enableRowGroup: false,
    hide: true,
  };

  public readonly MITRE_TECHNIQUE: ColDef = {
    headerName: this.findingsTexts.mitre_techniques,
    colId: PolicyFindingFilterField.MitreTechniques,
    field: 'mitreTechniques',
    initialWidth: 200,
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
    enableRowGroup: false,
    hide: true,
  };

  public readonly SRC_ASSETS: ColDef = {
    headerName: this.findingsTexts.source_asset,
    colId: 'srcNames',
    field: 'srcAssets',
    valueGetter: params => Utils.stripConnection(params?.data?.srcAssets),
    valueFormatter: params => GridFormatterService.valueSeparatorFormatter(params.value.map((asset: {name: string}) => asset.name)),
    tooltipValueGetter: params => params.value.map((asset: {name: string}) => asset.name).toString(),
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
    sortable: false,
    enableRowGroup: false,
    initialWidth: 200,
  };

  public readonly SRC_IP: ColDef = {
    headerName: this.findingsTexts.source_ip,
    colId: PolicyFindingFilterField.SrcIps,
    field: 'srcIps',
    initialWidth: 200,
    enableRowGroup: false,
    filter: SelectableFilterComponent,
    valueGetter: params => params?.data?.srcIps,
    filterParams: {
      keyComponentMap: useIpsKeyComponentMap(),
    } as SelectableFilterFilterParams<any>,
  };

  public readonly SRC_MACS: ColDef = {
    headerName: this.findingsTexts.source_mac,
    colId: PolicyFindingFilterField.SrcMacs,
    field: 'srcMacs',
    initialWidth: 200,
    enableRowGroup: false,
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
  };

  public readonly DST_ASSETS: ColDef = {
    headerName: this.findingsTexts.destination_asset,
    colId: 'dstNames',
    field: 'dstAssets',
    valueGetter: params => Utils.stripConnection(params?.data?.dstAssets),
    valueFormatter: params => GridFormatterService.valueSeparatorFormatter(params.value.map((asset: {name: string}) => asset.name)),
    tooltipValueGetter: params => params.value.map((asset: {name: string}) => asset.name).toString(),
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
    sortable: false,
    enableRowGroup: false,
    initialWidth: 200,
  };

  public readonly DST_IP: ColDef = {
    headerName: this.findingsTexts.destination_ip,
    colId: PolicyFindingFilterField.DstIps,
    field: 'dstIps',
    initialWidth: 200,
    enableRowGroup: false,
    filter: SelectableFilterComponent,
    valueGetter: params => params?.data?.dstIps,
    filterParams: {
      keyComponentMap: useIpsKeyComponentMap(),
    } as SelectableFilterFilterParams<any>,
  };

  public readonly DST_MACS: ColDef = {
    headerName: this.findingsTexts.destination_mac,
    colId: PolicyFindingFilterField.DstMacs,
    field: 'dstMacs',
    initialWidth: 200,
    enableRowGroup: false,
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
  };

  public readonly PROTOCOL: ColDef = {
    headerName: this.findingsTexts.protocol,
    colId: PolicyFindingFilterField.Protocols,
    field: 'protocols',
    enableRowGroup: false,
    initialWidth: 152,
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
  };

  public readonly FIRST_HIT: ColDef = {
    headerName: this.findingsTexts.first_hit,
    colId: PolicyFindingFilterField.FirstHitTime,
    field: 'firstHitTime',
    initialWidth: 200,
    hide: true,
    filter: TimeFilterSelectorComponent,
    enableRowGroup: false,
    valueGetter: (params) => params?.data?.firstHitTime && DateTimeService.utcToLocalFormatted(params.data?.firstHitTime, DatesFormatsEnum.general),
    filterParams: {
      componentMap: createFilterComponentMap(),
    },
  };

  public readonly LAST_HIT: ColDef = {
    headerName: this.findingsTexts.last_hit,
    colId: PolicyFindingFilterField.LastHitTime,
    field: 'lastHitTime',
    sort: 'desc',
    initialWidth: 200,
    filter: TimeFilterSelectorComponent,
    enableRowGroup: false,
    valueGetter: (params) => params.data?.lastHitTime && DateTimeService.utcToLocalFormatted(params.data?.lastHitTime, DatesFormatsEnum.general),
    filterParams: {
      componentMap: createFilterComponentMap(),
    },
  };

  public readonly ACTIVE_HITS: ColDef = {
    headerName: this.findingsTexts.active_hits,
    colId: PolicyFindingFilterField.ActiveHits,
    field: 'activeHits',
    enableRowGroup: false,
    filter: false,
    initialWidth: 120,
  };

  public readonly ASSET_TYPE: ColDef = {
    headerName: this.findingsTexts.asset_type,
    colId: PolicyFindingFilterField.AssetsTypes,
    field: 'assetsTypes',
    enableRowGroup: false,
    valueGetter: (params) => {
      return params?.data?.assetsTypes?.map((assetType: Maybe<AssetType>) => getAsset(assetType).name);
    },
    filterParams: {
      valueFormatter: (value: AssetType) => assetTypesMap.get(value)?.name ?? this.languageService.texts.general.key_words.blanks,
      customMap: GridUtils.mapFlat,
    },
    initialWidth: 200,
    hide: true,
  };

  public readonly ASSET_CRITICALITY: ColDef = {
    headerName: this.findingsTexts.asset_criticality,
    colId: PolicyFindingFilterField.AssetsCriticalities,
    field: 'assetsCriticalities',
    enableRowGroup: false,
    valueGetter: (params) => {
      return params?.data?.assetsCriticalities?.map((assetCriticality: Criticality) => this.languageService.formatEnum('Criticality', assetCriticality)).join(DelimitersEnum.pipe);
    },
    filterParams: {
      valueFormatter: (value: Criticality) => this.languageService.formatEnum('Criticality', value),
      customMap: GridUtils.mapFlat,
    },
    initialWidth: 120,
    hide: true,
  };

  public readonly ASSET_VENDOR: ColDef = {
    headerName: this.findingsTexts.asset_vendor,
    colId: PolicyFindingFilterField.AssetsVendors,
    field: 'assetsVendors',
    enableRowGroup: false,
    initialWidth: 120,
    filterParams: {
      customMap: GridUtils.mapFlat,
    },
    hide: true,
  };

  public readonly ASSET_FAMILY: ColDef = {
    headerName: this.findingsTexts.asset_family,
    colId: PolicyFindingFilterField.AssetsFamilies,
    field: 'assetsFamilies',
    enableRowGroup: false,
    initialWidth: 152,
    filterParams: {
      customMap: GridUtils.mapFlat,
    },
  };

  public readonly ASSET_MODEL: ColDef = {
    headerName: this.findingsTexts.asset_model,
    colId: PolicyFindingFilterField.AssetsModels,
    field: 'assetsModels',
    enableRowGroup: false,
    initialWidth: 152,
    filterParams: {
      customMap: GridUtils.mapFlat,
    },
    hide: true,
  };


  public readonly ASSET_TAGS: ColDef = {
    headerName: this.findingsTexts.asset_tags,
    // colId: PolicyFindingFilterField.AssetsTags,
    field: 'assetsTags',
    initialWidth: 200,
    enableRowGroup: false,
    sortable: false,
    filterParams: {
      valueFormatter: (value: string) => value === '' ? this.languageService.texts.general.key_words.blanks : value,
      customMap: GridUtils.mapFlat,
    },
  };

  public readonly PURDUE: ColDef = {
    ...this.inventoryUiGridService.PURDUE,
    headerName: this.findingsTexts.purdue_lvl,
    initialWidth: 104,
    hide: true,
    colId: PolicyFindingFilterField.AssetsPurdueLevels,
    field: 'assetsPurdueLevels',
    enableRowGroup: false,
    valueFormatter: (params: ValueFormatterParams) => params?.data?.assetsPurdueLevels?.map((assetsPurdueLevel: PurdueLevel) => this.languageService.formatEnum('PurdueLevel', assetsPurdueLevel)).join(DelimitersEnum.pipe),
    filterParams: {
      valueFormatter: (value: PurdueLevel) => {
        return this.languageService.formatEnum('PurdueLevel', value);
      },
      customMap: GridUtils.mapFlat,
    },
  };

  public readonly ASSET_LOCATION: ColDef = {
    headerName: this.findingsTexts.asset_location,
    colId: PolicyFindingFilterField.AssetsLocations,
    field: 'assetsLocations',
    enableRowGroup: false,
    initialWidth: 200,
    filterParams: {
      customMap: GridUtils.mapFlat,
    },
    hide: true,
  };

  public readonly RESOLVED_ON: ColDef = {
    headerName: this.findingsTexts.resolved_on,
    colId: PolicyFindingFilterField.ResolvedOn,
    field: 'resolvedOn',
    enableRowGroup: false,
    hide: true,
    filter: TimeFilterSelectorComponent,
    valueGetter: (params) => params?.data?.resolvedOn && DateTimeService.utcToLocalFormatted(params.data?.resolvedOn, DatesFormatsEnum.general),
    filterParams: {
      componentMap: createFilterComponentMap(),
    },
  };


  public readonly RESOLVED_BY: ColDef = {
    headerName: this.findingsTexts.resolved_by,
    colId: PolicyFindingFilterField.ResolvedUser,
    field: 'resolvedUser',
    initialWidth: 200,
    hide: true,
  };

  public readonly COMMENT: ColDef = {
    headerName: this.findingsTexts.comment,
    colId: 'comment',
    field: 'comment',
    initialWidth: 296,
    hide: true,
    enableRowGroup: false,
    filter: false,
    sortable: false,
  };


  constructor(
    private languageService: LanguageService,
    private inventoryUiGridService: InventoryUiGridService,
    private gridFormatterService: GridFormatterService,
    private pluginsGridSettingsService: PluginsGridSettingsService,
    private policyFindingsService: PolicyFindingsService,
  ) {
  }


  public findingsTransformFilterModel = (model: UiGridFilterModel<PolicyFindingFilterField>): UiGridFilterModel<PolicyFindingFilterField> => {
    switch (model.field) {
      case PolicyFindingFilterField.Protocols:
      case PolicyFindingFilterField.AssetsTypes:
      case PolicyFindingFilterField.AssetsCriticalities:
      case PolicyFindingFilterField.AssetsVendors:
      case PolicyFindingFilterField.AssetsFamilies:
      case PolicyFindingFilterField.AssetsModels:
      // case PolicyFindingFilterField.AssetsTags:
      case PolicyFindingFilterField.AssetsPurdueLevels:
      case PolicyFindingFilterField.AssetsLocations:
      case PolicyFindingFilterField.MitreTactics:
      case PolicyFindingFilterField.MitreTechniques:
      case PolicyFindingFilterField.SrcMacs:
      case PolicyFindingFilterField.DstMacs:
      case 'srcNames':
      case 'dstNames':
        return GridUtils.convertListFilter(model);
    }

    return model;
  };
}
